import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import '../styles/HomePage.css';
import MetaTags from '../components/MetaTags';

// Composants SVG inline pour les icônes
const IconCalendar = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
    <line x1="16" y1="2" x2="16" y2="6"></line>
    <line x1="8" y1="2" x2="8" y2="6"></line>
    <line x1="3" y1="10" x2="21" y2="10"></line>
  </svg>
);

const IconArrowRight = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12"></line>
    <polyline points="12 5 19 12 12 19"></polyline>
  </svg>
);

// Icônes unifiées pour les features
const IconCardinalHat = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2C8 2 5 4 4 6c-1 2-1 4 0 6 1 2 3 3 5 4 1 1 2 2 2 3v3h2v-3c0-1 1-2 2-3 2-1 4-2 5-4 1-2 1-4 0-6-1-2-4-4-8-4z"/>
    <path d="M8 14h8"/>
  </svg>
);

const IconVote = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
    <path d="M9 12l2 2 4-4"></path>
  </svg>
);

const IconCrown = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2 4l3 12h14l3-12-6 7-4-7-4 7-6-7zm3 16h14"></path>
  </svg>
);

const IconQuiz = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>
);

const HomePage = () => {
  const [stats, setStats] = useState({
    totalCardinaux: 0,
    votantsCount: 0,
    pays: 0,
    ageMoyen: 0
  });
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [articlesLoading, setArticlesLoading] = useState(true);
  const [error, setError] = useState(null);
  const [articlesError, setArticlesError] = useState(null);
  const [imageErrors, setImageErrors] = useState({});

  // Image de fallback pour les articles sans image
  const defaultImage = '/images/article-default.png';

  useEffect(() => {
    const fetchStats = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/cardinaux/stats');
        
        // Calculer l'âge moyen si nécessaire
        let ageMoyen = 0;
        if (response.data.ageStats && response.data.ageStats.length > 0) {
          let totalAge = 0;
          let totalCount = 0;
          
          response.data.ageStats.forEach(stat => {
            const avgAge = stat.range === "81+" 
              ? 85 // Estimation pour la tranche 81+
              : parseInt(stat.range.split('-')[0]) + 5; // Valeur moyenne de la tranche
              
            totalAge += avgAge * stat.count;
            totalCount += stat.count;
          });
          
          ageMoyen = Math.round(totalAge / totalCount);
        }
        
        const paysCount = response.data.paysServiceCounts ? response.data.paysServiceCounts.length : 0;
        
        setStats({
          totalCardinaux: response.data.totalCardinaux || 0,
          votantsCount: response.data.votantsCount || 0,
          pays: paysCount,
          ageMoyen: ageMoyen
        });
        
        setLoading(false);
      } catch (err) {
        console.error("Erreur lors du chargement des statistiques:", err);
        setError("Erreur lors du chargement des données");
        setLoading(false);
        
        // Valeurs par défaut en cas d'erreur
        setStats({
          totalCardinaux: 132,
          votantsCount: 83,
          pays: 68,
          ageMoyen: 73
        });
      }
    };

    const fetchArticles = async () => {
      try {
        setArticlesLoading(true);
        const response = await axios.get('/api/articles/featured?limit=3');
        setArticles(response.data);
        setArticlesLoading(false);
      } catch (err) {
        console.error('Erreur lors du chargement des articles:', err);
        setArticlesError('Impossible de charger les actualités');
        setArticlesLoading(false);
      }
    };
    
    fetchStats();
    fetchArticles();
  }, []);

  // Utiliser la route API pour les images avec le bon content-type
  const getApiImageUrl = (originalUrl, itemId) => {
    if (!originalUrl || imageErrors[itemId]) return defaultImage;
    
    const filename = originalUrl.split('/').pop().split('?')[0];
    const timestamp = new Date().getTime();
    return `/api/images/${filename}?t=${timestamp}`;
  };
  
  // Gestionnaire d'erreur pour les images
  const handleImageError = (itemId) => {
    console.log(`Erreur de chargement d'image pour l'item ${itemId}, utilisation de l'image par défaut`);
    setImageErrors(prev => ({
      ...prev,
      [itemId]: true
    }));
  };
  
  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '';
    
    try {
      const options = { day: 'numeric', month: 'long', year: 'numeric' };
      return new Date(dateString).toLocaleDateString('fr-FR', options);
    } catch (error) {
      console.error('Erreur de formatage de date:', error);
      return dateString;
    }
  };
  
  // Extraire un résumé court
  const getShortSummary = (text, maxLength = 100) => {
    if (!text) return '';
    
    if (text.length <= maxLength) {
      return text;
    }
    
    return text.substring(0, maxLength).trim() + '...';
  };

  // Rendu des articles dynamiques
  const renderArticles = () => {
    if (articlesLoading) {
      return (
        <div className="news-grid">
          {[1, 2, 3].map(i => (
            <div key={i} className="news-card loading-card">
              <div className="news-image-skeleton skeleton"></div>
              <div className="news-content">
                <div className="news-date-skeleton skeleton"></div>
                <div className="news-title-skeleton skeleton"></div>
                <div className="news-text-skeleton skeleton"></div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (articlesError || articles.length === 0) {
      // Articles statiques de fallback
      return (
        <div className="news-grid">
          <div className="news-card">
            <div className="news-image">
              <img src={defaultImage} alt="Actualité" />
            </div>
            <div className="news-content">
              <div className="news-date">
                <IconCalendar /> 15 Avril 2025
              </div>
              <h3>Nomination de trois nouveaux cardinaux</h3>
              <p>Le Pape a annoncé la nomination de trois nouveaux cardinaux lors de son audience générale...</p>
            </div>
          </div>
          <div className="news-card">
            <div className="news-image">
              <img src={defaultImage} alt="Actualité" />
            </div>
            <div className="news-content">
              <div className="news-date">
                <IconCalendar /> 2 Avril 2025
              </div>
              <h3>Décès du Cardinal Giovanni Battista Re</h3>
              <p>Le doyen du Conclave 2025 s'est éteint à l'âge de 91 ans...</p>
            </div>
          </div>
          <div className="news-card">
            <div className="news-image">
              <img src={defaultImage} alt="Actualité" />
            </div>
            <div className="news-content">
              <div className="news-date">
                <IconCalendar /> 28 Mars 2025
              </div>
              <h3>Réunion du Conseil des cardinaux</h3>
              <p>Le C9 s'est réuni pour discuter des réformes en cours de la Curie romaine...</p>
            </div>
          </div>
        </div>
      );
    }

    // Affichage des articles dynamiques
    return (
      <div className="news-grid">
        {articles.map(article => (
          <Link to={`/actualites/${article.slug}`} className="news-card" key={article._id}>
            <div className="news-image">
              <img 
                src={getApiImageUrl(article.image, article._id)} 
                alt={article.titre}
                onError={() => handleImageError(article._id)}
              />
            </div>
            <div className="news-content">
              <div className="news-date">
                <IconCalendar /> {formatDate(article.datePublication)}
              </div>
              <h3>{article.titre}</h3>
              <p>{getShortSummary(article.resume)}</p>
            </div>
          </Link>
        ))}
      </div>
    );
  };

  return (
    <div className="home-page">
      <MetaTags 
        title="Accueil" 
        description="Explorez l'histoire et découvrez les membres actuels du Sacré Collège des Cardinaux" 
        url="/"
      />
  
      <div className="hero-section">
        <div className="hero-content">
          <h1>Bienvenue sur le site Conclave2025.fr</h1>
          <p className="hero-subtitle">Explorez l'histoire et découvrez les membres actuels du Sacré Collège</p>
          <div className="hero-buttons">
            <Link to="/cardinaux" className="btn btn-primary">Annuaire des Cardinaux</Link>
            <Link to="/conclave" className="btn btn-secondary">Conclave Virtuel</Link>
          </div>
        </div>
      </div>
      
      <div className="container">
        <section className="stats-section">
          <h2>Le Collège en chiffres</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-number">{loading ? '...' : stats.totalCardinaux}</span>
              <span className="stat-label">Cardinaux</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{loading ? '...' : stats.votantsCount}</span>
              <span className="stat-label">Électeurs</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{loading ? '...' : stats.pays}</span>
              <span className="stat-label">Pays</span>
            </div>
            <div className="stat-card">
              <span className="stat-number">{loading ? '...' : stats.ageMoyen}</span>
              <span className="stat-label">Âge moyen</span>
            </div>
          </div>
          {error && <p className="error-message">{error}</p>}
        </section>

        <section className="news-section">
          <div className="section-title-container">
            <h2>Actualités du Sacré Collège</h2>
          </div>
          {renderArticles()}
          <div className="view-all-news-container">
            <Link to="/actualites" className="view-all-news-button">
              Voir toutes les actualités <IconArrowRight />
            </Link>
          </div>
        </section>

        <section className="features-section">
          <h2>Découvrez nos fonctionnalités</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">
                <IconCardinalHat />
              </div>
              <h3>Annuaire des Cardinaux</h3>
              <p>
                Consultez les profils détaillés de tous les cardinaux avec leurs
                biographies, photos et fonctions actuelles.
              </p>
              <Link to="/cardinaux" className="feature-link">
                Explorer l'annuaire
              </Link>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <IconVote />
              </div>
              <h3>Conclave Virtuel</h3>
              <p>
                Participez à notre simulation interactive du conclave et votez
                pour les cardinaux que vous considérez comme papabile.
              </p>
              <Link to="/conclave" className="feature-link">
                Rejoindre le conclave
              </Link>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <IconCrown />
              </div>
              <h3>Papabili du Jour</h3>
              <p>
                Découvrez chaque jour un cardinal papabile mis en avant avec
                une analyse de ses atouts pour le prochain conclave.
              </p>
              <Link to="/papabile-du-jour" className="feature-link">
                Voir le papabile du jour
              </Link>
            </div>
            
            <div className="feature-card">
              <div className="feature-icon">
                <IconQuiz />
              </div>
              <h3>Quiz sur le Conclave</h3>
              <p>
                Testez vos connaissances sur l'histoire des conclaves, le Vatican
                et le Collège des Cardinaux avec nos quiz interactifs.
              </p>
              <Link to="/quiz" className="feature-link">
                Jouer au quiz
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default HomePage;